










































import {
  Component,
  Vue,
  Emit,
  PropSync,
  Watch,
} from "vue-property-decorator";
import * as farmsService from "@/services/farms";
import { farmDTO as farmDTOFactory } from "@/api/farms/types";
import FarmDrawingModal from "@/views/farms/components/farm-controller/FarmDrawingModal.vue";
import FarmDrawConfirm from "./FarmDrawConfirm.vue";
import Geometry from "ol/geom/Geometry";
import { getAreaInHectares, getCentroid, intersectsItself } from "@/helpers/geometry";
import ConfirmationModal from "@/components/confirmationModal/ConfirmationModal.vue";
import * as locationService from "@/services/location";
import { CityRequest } from "@/api/location/types";
import CarFindModal from "@/views/farms/components/car-find-modal/CarFindModal.vue";
import { RuralInfo } from "@/api/car/types";
import { Owner } from "@/api/owner/types";
import OwnerSelect from './OwnerSelect.vue';
import * as notificationService from "@/services/notification";

const STORAGE_KEY = '_farms-home-location';
const TAB_KEY = "_farms-home-tab-open";

@Component({
  name: "FarmController",
  components: {
    FarmDrawingModal,
    FarmDrawConfirm,
    ConfirmationModal,
    CarFindModal,
    OwnerSelect,
  },
})
export default class FarmController extends Vue {
  @PropSync("state", { type: String, default: "closed" })
  private stateSynced!: string;

  private farmGeometry: Geometry | null = null;
  private farmArea: number | null = null;
  private farmName: string = '';
  private farmCarCode: string = '';
  private isCarFindModalVisible: boolean = false;
  private carResponse: RuralInfo | null = null;
  private startLocation: any = null;

  private owner: Owner | null = null;

  private get farmProps() {
    return {
      farmName: this.farmName,
      cityName: this.cityFromCentroid?.cityName ?? "",
      stateAcronym: this.cityFromCentroid?.stateAcronym ?? "",
      area: this.formattedAreaInHa,
    };
  }

  private get cityFromCentroid() {
    return this.$store.state.location.cityFromCentroid;
  }

  private get formattedAreaInHa() {
    if (!this.farmArea) {
      return "";
    }
    return `${this.$n(this.farmArea, "rounded-number")} ha`;
  }

  private created() {
    this.startLocation = undefined;
  }

  @Watch('stateSynced')
  private onMapOpen(stateSynced:string) {
      if (stateSynced === 'initial') {
      const loadedTab = localStorage.getItem(TAB_KEY);
      const tabOpen = loadedTab ? JSON.parse(loadedTab) : loadedTab;
      if (tabOpen === 'map') {
        const loadedLocation = localStorage.getItem(STORAGE_KEY);
        this.startLocation = loadedLocation ? JSON.parse(loadedLocation) : loadedLocation;
      } else {
        this.startLocation = undefined;
      }
    }
  }

  private get confirmationText() {
    const message = !!this.owner
      ? "farms.farm-controller.confirmation-label"
      : "farms.farm-controller.confirmation-without-owner-label"
    return this.$t(message, {
      farmName: this.farmName,
      ownerName: this.owner?.name,
    });
  }

  private get isMapVisible() {
    return this.stateSynced !== "closed";
  }

  private get isMapDisabled() {
    return this.stateSynced !== "initial";
  }

  private get isFarmNameModalOpen() {
    return this.stateSynced === "draw:complete";
  }

  private get isOwnerSelectOpen() {
    return this.stateSynced === 'farm-name:complete'
  }

  private get isConfirmationOpen() {
    return this.stateSynced === "owner-select:complete";
  }

  private async finishDrawing(farmGeometry: Geometry) {
    const isValid = !intersectsItself(farmGeometry);
    if (isValid) { 
      this.farmGeometry = farmGeometry;
      this.farmArea = getAreaInHectares(farmGeometry).value;
      locationService.fetchCityByCentroid(
        CityRequest.withCentroid(getCentroid(farmGeometry))
      );
      this.goToFarmNameStep();
    } else {
      (this.$refs.drawingModal as any).clearDrawing();
      notificationService.addErrorNotification(this.$t("notifications.error.invalid-geometry-intersection") as string);
    } 
  }

  @Emit("create:farm")
  private onCreateSuccess() {
    this.stateSynced = "closed";
    this.resetDrawing();
  }

  @Emit("cancel")
  private cancelCreation() {
    this.resetDrawing();
    this.stateSynced = "closed";
  }

  private goToDrawingStep() {
    this.resetDrawing();
    this.stateSynced = "initial";
  }

  private resetDrawing() {
    this.farmName = ''
    this.farmArea = null;
    this.farmGeometry = null;
    this.farmCarCode = '';
    this.carResponse = null;
    this.owner = null;
    (this.$refs.drawingModal as any).clearDrawing();
  }

  private goToFarmNameStep() {
    this.stateSynced = "draw:complete";
  }

  private selectedFarmName(farmName: string) {
    this.farmName = farmName;
    this.stateSynced = "farm-name:complete";
  }

  private goToOwnerSelectStep() {
    this.stateSynced = 'farm-name:complete';
  }

  private selectedOwner(owner: Owner | null) {
    this.owner = owner;
    this.stateSynced = 'owner-select:complete';
  }

  private async createFarm() {
    const farm = farmDTOFactory(
        this.farmName!,
        this.owner?.uuid ?? null,
        this.farmGeometry!,
        this.farmCarCode);

    try {
      this.stateSynced = "creating:farm";
      await farmsService.createFarmWithoutStore(farm);
      this.onCreateSuccess();
    } catch(err) {
      this.goToFarmNameStep();
    }
  }

  private showCarModal() {
    this.isCarFindModalVisible = true;
  }

  private cancelFindByCar() {
    this.isCarFindModalVisible = false;
  }

  private getFarmByCarCodeResponse(response: RuralInfo) {
    this.carResponse = response;
    this.farmCarCode = response.ruralCode ?? '';
    this.farmName = this.farmCarCode;
    this.isCarFindModalVisible = false;
  }
}
